/**
 * QueryErrorState Component
 *
 * Standard error state for data-fetching surfaces. Renders a message plus a
 * "Try Again" action that re-runs the failed request via React Query's
 * `refetch` (passed as `onRetry`) instead of a full-page `window.location.reload()`,
 * which would discard SPA state and other cached queries.
 *
 * See docs/guides/developer/frontend-responsive-loading-patterns.md.
 */

import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";

interface QueryErrorStateProps {
	/** Re-run the failed query. Typically the `refetch` returned by useQuery. */
	onRetry?: () => void;
	/** Optional heading shown above the message (mirrors EmptyState's title). */
	title?: string;
	/** Optional override message. Falls back to a translated generic message. */
	message?: string;
	/**
	 * When true, the retry button is disabled and shows a spinner with a loading label — pass
	 * `isFetching`/`isRefetching` from the query so rapid clicks can't stack
	 * multiple refetches.
	 */
	isRetrying?: boolean;
	className?: string;
}

export function QueryErrorState({
	onRetry,
	title,
	message,
	isRetrying = false,
	className,
}: QueryErrorStateProps) {
	const { t } = useTranslation();

	return (
		<div
			className={`flex flex-col items-center justify-center py-12 text-center ${className ?? ""}`}
			role="alert"
		>
			{title && (
				<h3 className="mb-1 text-lg font-semibold text-foreground">{title}</h3>
			)}
			<p className="text-destructive">
				{message ?? t("common.somethingWentWrong", "Something went wrong")}
			</p>
			{onRetry && (
				<Button
					variant="outline"
					onClick={onRetry}
					disabled={isRetrying}
					className="mt-4"
				>
					{isRetrying ? (
						<>
							<Loader2 className="mr-2 h-4 w-4 animate-spin text-dashboard-muted" />
							{t("common.loading", "Loading...")}
						</>
					) : (
						t("common.tryAgain", "Try Again")
					)}
				</Button>
			)}
		</div>
	);
}
